<template>
  <div :class="'vuedatepicker ' + ' product-order-' + field + (clearable ? ' clearable' : '') + (date ? ' has-value' : '')">
    <label :for="labelId" :class="'product-order-datetitle js-product-' + field + 'title' + (floatingLabel ? ' floating-label' : '') + (date || (floatingLabel && floatingLabel === 'forced') ? ' has-value' : '')" :id="labelId" v-if="title">{{ title }}</label>
    <VueDatePicker
        v-if="isLocaleReady"
        v-model="date" 
        ref="picker"
        :min-date="actualMinDate" 
        :max-date="maxDate"  
        :auto-apply="true" 
        :time-picker="dateFormat.includes('y') ? false : true"
        :time-config="{ 
          enableTimePicker: timePicker ? true : false,
          timePickerInline: timePickerInline ? true : false 
        }"
        :input-attrs="{
          hideInputIcon: true,
          clearable: clearable,
        }"
        :formats="{ input: dateFormat, preview: dateFormat }" 
        :placeholder="placeholder" 
        :locale="dateFnsLocale"
        :ui="{ ...datePickerClasses, dayClass: (day, internalModel) => getDayClass(day) }"
        :timezone="timezone" 
        :allowed-dates="allowedDates"
        :teleport="true" 
        :no-today="staticInput ? false : true"
        :six-weeks="true"
        :text-input="{
          selectOnFocus: true,
          applyOnBlur: !staticInput
        }"
        @open="handleOpen" 
        @closed="handleClose" 
        @update-month-year="handleMonthChange"
    >
    </VueDatePicker>
  </div>
</template>

<script>
import { markRaw } from 'vue'
import { DateTime } from 'luxon'
import { mapGetters } from 'vuex'
import { isChunkLoadError, reloadOnceOnChunkError } from '~/lib/chunkLoadError'

// Explicit import() per language so Vite emits one chunk each instead of
// bundling every date-fns locale into the datepicker.
// When a chunk preload fails, the vite:preloadError handler (plugins/00-chunk-reload) prevents the error and
// Vite resolves the import with undefined, so the module is read defensively.
const pickLocale = (m, name) => (m ? m[name] || m.default : undefined)
const dateFnsLocaleLoaders = {
  de: () => import('date-fns/locale/de').then(m => pickLocale(m, 'de')),
  en: () => import('date-fns/locale/en-US').then(m => pickLocale(m, 'enUS')),
  es: () => import('date-fns/locale/es').then(m => pickLocale(m, 'es')),
  fi: () => import('date-fns/locale/fi').then(m => pickLocale(m, 'fi')),
  fr: () => import('date-fns/locale/fr').then(m => pickLocale(m, 'fr')),
  it: () => import('date-fns/locale/it').then(m => pickLocale(m, 'it')),
  nb: () => import('date-fns/locale/nb').then(m => pickLocale(m, 'nb')),
  no: () => import('date-fns/locale/nb').then(m => pickLocale(m, 'nb')),
  sv: () => import('date-fns/locale/sv').then(m => pickLocale(m, 'sv'))
}

const dateFnsLocaleCache = {}

// Resolves to undefined when the locale can't be loaded; the picker then uses its built-in English locale
function loadDateFnsLocale (name) {
  const key = dateFnsLocaleLoaders[name] ? name : 'en'
  if (!dateFnsLocaleCache[key]) {
    dateFnsLocaleCache[key] = dateFnsLocaleLoaders[key]()
      .catch(error => {
        if (isChunkLoadError(error)) {
          reloadOnceOnChunkError()
        }
        return undefined
      })
      .then(locale => {
        if (!locale) {
          delete dateFnsLocaleCache[key] // Try again on the next picker instead of caching the failure
          return undefined
        }
        return markRaw(locale)
      })
  }
  return dateFnsLocaleCache[key]
}

export default {
  data () {
    return {
      picker: null,
      currentMonth: undefined,
      currentYear: undefined,
      why: undefined,
      identifier: '',
      productId: undefined,
      availableDates: [],
      note: undefined,
      dateFnsLocale: undefined,
      isLocaleReady: false
    }
  },
  props: ['modelValue', 'timePicker', 'timePickerInline', 'format', 'title', 'placeholder', 'shortLocale', 'startDate', 'value', 'minDate', 'maxDate', 'notBefore', 'notAfter', 'product', 'resourceCount', 'reservationCount', 'pricing', 'origin', 'destination', 'linktype', 'staticInput', 'floatingLabel', 'clearable', 'requiredResources', 'field', 'tz'],
  emits: ['update:modelValue', 'closed'],
  computed: {
    ...mapGetters(['allSettings', 'shopId', 'locale']),
    timezone () {
      return this.tz || this.$store.state.timezone
    },
    settings () {
      return this.shopSettings(this.shopId)
    },
    pickerLocaleName () {
      const raw = this.shortLocale || this.locale || 'en'
      return String(raw).replace(/_/g, '-').split('-')[0].toLowerCase()
    },
    date: {
      get () {
        if (!this.modelValue) {
          return undefined
        }
        return this.modelValue
      },
      set (newVal) {
        // VueDatePicker emits TZDate instances and may mutate them in place.
        // Always hand the parent a plain Date copy so Vue sees a new reference
        // and the picker does not share (or later rewrite) the stored value.
        if (newVal instanceof Date) {
          this.$emit('update:modelValue', new Date(newVal.getTime()))
          return
        }
        this.$emit('update:modelValue', newVal)
      }
    },
    dateFormat() {
      let ret = ''
      if (this.format) {
        ret = this.format
      }
      if (this.settings.dateformat) {
        if (this.timePicker) {
          ret = this.settings.dateformat + ' ' + this.settings.timeformat
        } else {
          ret = this.settings.dateformat
        }
      }
      if (ret) {
        return ret.replace(/D/g, 'd').replace(/Y/g, 'y')
      }
      switch (this._('YYYY/MM/DD')) {
        case 'D.M.YYYY':
          return 'd.M.yyyy'
        default:
          return 'yyyy/MM/dd'
      }
    },
    actualMinDate () {
      return this.minDate ? this.minDate : DateTime.fromSeconds(0).toJSDate()
    },
    minDateOrToday () {
      const targetDate = DateTime.now().setZone(this.timezone)
      const localDate = DateTime.fromObject({
        year: targetDate.year,
        month: targetDate.month,
        day: targetDate.day
      }).startOf('day')
      return this.minDate ? DateTime.fromJSDate(this.minDate).startOf('day').toJSDate() : localDate.toJSDate()
    },
    currentDate () {
      let date = DateTime.now().setZone(this.timezone).startOf('day')
      date = date.set({ day: 1 })
      if (this.currentMonth || this.currentMonth === 0) {
        date = date.set({ month: this.currentMonth + 1 })
      }
      if (this.currentYear) {
        date = date.set({ year: this.currentYear })
      }
      return date.toJSDate()
    },
    labelId () {
      return 'product-input-' + this.field + '-' + this.identifier
    },
    allowedDates () {
      if (this.staticInput || !this.productId) {
        return undefined
      } else {
        if (this.hasPosBasket) {
          return undefined
        }
        if (this.availableDates) {
          return this.availableDates.filter(d => d[1] >= 0).map(d => DateTime.now().startOf('day').setZone(this.timezone).set({ month: this.currentMonth + 1, year: this.currentYear, day: d[0] }).toJSDate())
        }
      }
      return []
    },
    reservedDates () {
      if (this.staticInput || !this.productId) {
        return undefined
      } else {
        if (this.availableDates) {
          return this.availableDates.filter(d => d[1] < 0).map(d => DateTime.now().startOf('day').setZone(this.timezone).set({ month: this.currentMonth + 1, year: this.currentYear, day: d[0] }).toJSDate())
        }
      }
      return []
    },
    menuClassName () {
      if (this.allowedDates === undefined || this.staticInput || !this.productId) {
        return ''
      }
      if (!this.allowedDates.length) {
        if (this.reservedDates?.length) {
          return 'fully-booked'
        }
        return 'no-dates'
      }
      return ''
    },
    datePickerClasses () {
      return {
        menu: this.menuClassName,
        input: 'form-control js-datepicker-input'
      }
    },
    fullyBookedText () {
      return this._('This month is fully booked.')
    },
    noDatesText () {
      return this._('There are no dates that can be selected this month.')
    }
  },
  methods: {
    shopSettings (shopId) {
      return this.allSettings[shopId + '-' + this.locale] || {}
    },
    async selectNextAvailability () {
      const nextAvailableTs = await this.loadNextAvailability()
      if (nextAvailableTs) {
        const d = DateTime.fromSeconds(nextAvailableTs).setZone(this.timezone)
        this.picker?.setMonthYear({ month: d.month - 1, year: d.year })
        this.picker?.onScroll()
      } else {
        this.$toast.error(_('We could not find anything matching your criteria.'))
      }
    },
    getDayClass (currDate) {
      if (!(currDate instanceof Date)) {
        return ''
      }
      if (this.currentMonth !== currDate.getMonth() || this.currentYear !== currDate.getFullYear()) {
        return ''
      }
      const dateStart = DateTime.fromJSDate(currDate).startOf('day').toJSDate()
      if (this.date instanceof Date && currDate.toDateString() === this.date.toDateString()) {
        return ''
      }
      if (currDate < this.actualMinDate) {
        return ''
      }
      if (this.maxDate && DateTime.fromJSDate(this.maxDate).startOf('day').toJSDate() < dateStart) {
        return ''
      }
      if (this.staticInput || !this.productId) {
        return ''
        // return dateStart >= this.actualMinDate ? 'dp__cell_highlight' : ''
      } else if (this.availableDates) {
        const rule = this.availableDates.find(d => d[0] === currDate.getDate())
        if (rule) {
          switch (rule[1]) {
            case 1:
              return 'dp__selectable'
            case 0:
              return 'dp__partial'
            case -1:
              return 'dp__reserved'
          }
        }
        return 'dp__notselectable'
      }
      return ''
    },
    handleMonthChange ({ instance: _, month, year }) {
      this.currentMonth = month
      this.currentYear = year
    },
    async handleOpen () {
      const d = this.date ?? this.value ?? this.minDateOrToday
      if (d instanceof Date) {
        this.currentMonth = d.getMonth()
        this.currentYear = d.getFullYear()
      }
      this.productId = this.product?.id
      this.why = undefined
      await nextTick()
      if (d instanceof Date) {
        this.picker?.setMonthYear({ month: d.getMonth(), year: d.getFullYear() })
      }
      this.picker?.onScroll()
    },
    open () {
      const picker = this.$refs.picker
      if (picker && picker.openMenu) {
        picker.openMenu()
      }
    },
    async handleClose () {
      await nextTick()
      if (!this.why) {
        // Not resetting productId.value will enable availabledates queries when the selector is closed
        // This is needed when doing the "why" query
        this.productId = null
      }
      this.$emit('closed')
    },
    async resolveDateFnsLocale (name) {
      this.isLocaleReady = false
      const locale = await loadDateFnsLocale(name)
      if (this.pickerLocaleName !== name) {
        return
      }
      this.dateFnsLocale = locale
      this.isLocaleReady = true
    }
  },
  watch: {
    pickerLocaleName: {
      immediate: true,
      handler (name) {
        this.resolveDateFnsLocale(name)
      }
    },
    date (newVal) {
      let actuallyAllowedDates = this.availableDates.filter(d => d[1] >= 0).map(d => DateTime.now().startOf('day').setZone(this.timezone).set({ month: this.currentMonth + 1, year: this.currentYear, day: d[0] }).toISO())
      const compare = DateTime.fromJSDate(newVal).startOf('day').setZone(this.timezone).toISO()
      if (newVal && actuallyAllowedDates && !actuallyAllowedDates.includes(compare)) {
        this.why = DateTime.fromJSDate(newVal).startOf('day').setZone(this.timezone).toFormat('yyyy-MM-dd')
        return
      }
    },
    note (newVal) {
      if (newVal) {
        this.$toast.error(newVal, { autoClose: 10000 })
        this.date = undefined
      }
    }
  }
}

</script>

<style scoped>
.next-availability {
  display: none;
  position: relative;
  z-index: 100000;
}
.no-dates .next-availability {
  display: block;
}
</style>

<style>
.vuedatepicker .dp--clear-btn {
  right: 35px;
}
/* The calendar icon is hidden once there's a value, so the clear button takes its place instead of covering the text */
.vuedatepicker.clearable.has-value .dp--clear-btn {
  right: 0;
}
.vuedatepicker .form-control {
  color: rgb(69, 89, 100) !important;
}
.dp__calendar_item {
  --dp-text-color: #000 !important;
}
</style>